/** 奖品编辑、成就规则奖励编辑（与 iOS AchieveSheets 一致） */
import { ImagePlus } from "lucide-react";
import { useRef, useState } from "react";
import { type AchievementRule, confirm, deleteEntity, didWrite, type Reward, run, toast, upload, useChildren, useStore, writeEntity } from "../../core";
import { CardPhoto } from "../../components/cards/CardPhotos";
import { Button, FormCard, KPIcon, ModalHeader, NumberStepper, REWARD_ICON_KEYS, RowDivider, SectionLabel, Sheet, SheetBody, SheetHeader, Spinner, Switch, TextField, ValueRow } from "../../ui";
import { ruleCondition, withPoints } from "./ruleText";

/** reward = null 为新建 */
export function RewardEditSheet({ open, reward, onClose }: { open: boolean; reward: Reward | null; onClose: () => void }) {
  return (
    <Sheet open={open} onClose={onClose} persistent label="奖品">
      {open && <RewardForm key={reward?.id ?? "new"} reward={reward} onClose={onClose} />}
    </Sheet>
  );
}

function RewardForm({ reward, onClose }: { reward: Reward | null; onClose: () => void }) {
  const [name, setName] = useState(reward?.name ?? "");
  const [icon, setIcon] = useState(reward?.icon ?? "gift");
  const [cost, setCost] = useState(reward?.costPoints ?? 50);
  const [limited, setLimited] = useState(reward?.stock != null);
  const [stock, setStock] = useState(reward?.stock ?? 1);
  const [enabled, setEnabled] = useState(reward?.enabled ?? true);
  const [image, setImage] = useState<string | null>(reward?.imageAssetId ?? null);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    // stock 传 null 表示不限量、image_asset_id 传 null 表示去掉图片（PATCH 时用来清空）
    const body = { name: name.trim(), icon, costPoints: cost, stock: limited ? stock : null, enabled, imageAssetId: image };
    const r = await run(() => (reward ? writeEntity("rewards", "PATCH", `rewards/${reward.id}`, body) : writeEntity("rewards", "POST", "rewards", body)), "已保存");
    setBusy(false);
    if (r) onClose();
  };

  const remove = async () => {
    if (!reward) return;
    if (!(await confirm({ title: `删除奖品「${reward.name}」？`, confirmText: "删除", danger: true }))) return;
    setBusy(true);
    const ok = await run(async () => {
      await deleteEntity("rewards", reward.id, `rewards/${reward.id}`);
      return true;
    }, "已删除");
    setBusy(false);
    if (ok) onClose();
  };

  return (
    <>
      <ModalHeader title={reward ? "编辑奖品" : "新奖品"} actionTitle="保存" actionEnabled={!!name.trim() && !uploading} busy={busy} onCancel={onClose} onAction={() => void save()} />
      <SheetBody gap={12}>
        <TextField label="名称" value={name} onChange={setName} placeholder="例如：周末去公园" data-testid="reward-name" />
        <FormCard>
          <RewardImagePicker value={image} onChange={setImage} uploading={uploading} onUploading={setUploading} />
        </FormCard>
        <SectionLabel>{image ? "图标（没有图片时显示）" : "图标"}</SectionLabel>
        <div className="achieve-icons">
          {REWARD_ICON_KEYS.map((k) => (
            <button key={k} type="button" className={`achieve-icon ${icon === k ? "is-on" : ""}`} aria-pressed={icon === k} aria-label={k} onClick={() => setIcon(k)}>
              <KPIcon name={k} size={20} />
            </button>
          ))}
        </div>
        <FormCard>
          <ValueRow title="所需积分" height={56}>
            <NumberStepper value={cost} min={1} max={9999} step={10} onChange={setCost} />
          </ValueRow>
          <RowDivider />
          <Switch label="限量" checked={limited} onChange={setLimited} />
          {limited && (
            <>
              <RowDivider />
              <ValueRow title="库存" height={56}>
                <NumberStepper value={stock} min={0} max={999} onChange={setStock} />
              </ValueRow>
            </>
          )}
          <RowDivider />
          <Switch label="上架（孩子可以申请）" checked={enabled} onChange={setEnabled} />
        </FormCard>
        {reward?.enabled && <GoalSwitches reward={reward} />}
        {reward && (
          <Button variant="ghost" block className="achieve-danger-btn" disabled={busy} onClick={() => void remove()}>
            删除奖品
          </Button>
        )}
      </SheetBody>
    </>
  );
}

/** 奖品图片：选一张就上传；有图片时孩子在积分商城看到的是图片（与 iOS RewardImagePicker 一致） */
function RewardImagePicker({ value, onChange, uploading, onUploading }: { value: string | null; onChange: (id: string | null) => void; uploading: boolean; onUploading: (b: boolean) => void }) {
  const input = useRef<HTMLInputElement>(null);
  const pick = async (file: File | undefined) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast("只能选图片", { error: true });
      return;
    }
    onUploading(true);
    try {
      const asset = await upload(file);
      didWrite("assets", asset);
      onChange(asset.id);
    } catch (e) {
      toast(`图片上传失败：${e instanceof Error ? e.message : String(e)}`, { error: true });
    } finally {
      onUploading(false);
      if (input.current) input.current.value = "";
    }
  };
  return (
    <div className="achieve-reward-image">
      <button type="button" className="achieve-reward-image-tile" disabled={uploading} onClick={() => input.current?.click()} aria-label={value ? "换一张图片" : "加奖品图片"} data-testid="reward-image">
        {value ? <CardPhoto assetId={value} size={88} /> : !uploading && <ImagePlus size={26} strokeWidth={2} />}
        {uploading && <Spinner size={20} />}
      </button>
      <div>
        <strong>奖品图片</strong>
        <p>{uploading ? "上传中…" : value ? "点图片可以换一张" : "可选。拍一张奖品的照片，孩子在积分商城看到的就是它"}</p>
        {value && !uploading && (
          <button type="button" className="achieve-reward-image-del" onClick={() => onChange(null)}>
            去掉图片
          </button>
        )}
      </div>
      <input ref={input} type="file" accept="image/*" hidden onChange={(e) => void pick(e.target.files?.[0])} />
    </div>
  );
}

/** 设为孩子的积分目标（PUT /children/{id}/goal，与 iOS RewardEditSheet 一致）；孩子也能在平板上自己换 */
function GoalSwitches({ reward }: { reward: Reward }) {
  const children = useChildren();
  if (!children.length) return null;
  const setGoal = (childId: string, on: boolean) =>
    void run(() => writeEntity("children", "PUT", `children/${childId}/goal`, { rewardId: on ? reward.id : null }));
  return (
    <>
      <SectionLabel>积分目标</SectionLabel>
      <FormCard>
        {children.map((c, i) => (
          <div key={c.id}>
            {i > 0 && <RowDivider />}
            <Switch label={`设为${c.name}的目标`} checked={c.settings?.goalRewardId === reward.id} onChange={(v) => setGoal(c.id, v)} />
          </div>
        ))}
      </FormCard>
      <p className="achieve-note">孩子平板首页和积分商城会显示离目标还差多少分；孩子也可以在平板上自己换目标。</p>
    </>
  );
}

/** 修改成就规则的奖励积分（成就只发积分，卡牌由家长发） */
export function RuleEditSheet({ rule, onClose }: { rule: AchievementRule | null; onClose: () => void }) {
  return (
    <Sheet open={!!rule} onClose={onClose} width={440} label="成就规则">
      {rule && <RuleForm key={rule.id} rule={rule} onClose={onClose} />}
    </Sheet>
  );
}

function RuleForm({ rule, onClose }: { rule: AchievementRule; onClose: () => void }) {
  const initial = typeof (rule.reward as { points?: unknown } | null)?.points === "number" ? ((rule.reward as { points: number }).points) : 0;
  const [points, setPoints] = useState(initial);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    const r = await run(() => writeEntity("achievementRules", "PATCH", `achievement-rules/${rule.id}`, { reward: withPoints(rule.reward, points) }), "已修改奖励");
    setBusy(false);
    if (r) onClose();
  };

  return (
    <>
      <SheetHeader title={rule.name} onClose={onClose} />
      <SheetBody gap={14}>
        <p className="achieve-rule-cond">{ruleCondition(rule)}</p>
        {rule.description && <p className="achieve-note">{rule.description}</p>}
        <div className="achieve-points-box">
          <span>奖励积分</span>
          <NumberStepper value={points} min={0} max={500} step={5} onChange={setPoints} />
        </div>
        <p className="achieve-note">成就只加分；想给孩子发卡，在成就页点「发一张卡」。</p>
        <Button variant="dark" block busy={busy} onClick={() => void save()}>
          保存
        </Button>
      </SheetBody>
    </>
  );
}
